/**
 * Frontend mirror of the agent-service label table. Used as a fallback
 * when the server omits a `label` (older agent versions, malformed
 * events). Keep in sync with
 * packages/agent-service/src/agent_service/services/activity_labels.py.
 */

const FALLBACK = 'Working on it';

const TOOL_LABELS: Readonly<Record<string, string>> = {
  // Local agent-service tools
  web_search: 'Searching the web',
  capture_photo: 'Capturing a photo',
  // ADK's memory tool (Memory Bank similarity search)
  load_memory: 'Recalling memory',
  // Deterministic science tools (agent-service tools/sequence_analysis.py, tools/bio_lookup.py)
  sequence_analysis: 'Analyzing the sequence',
  bio_lookup: 'Looking it up in a public database',
  // Workspace search
  unified_search: 'Searching your workspace',
  // Artifacts
  find_artifact_by_title: 'Finding a note',
  list_artifacts: 'Browsing your notes',
  get_artifact: 'Reading a note',
  create_artifact: 'Creating a note',
  update_artifact: 'Updating a note',
  merge_artifacts: 'Merging notes',
  move_artifact_to_entity: 'Moving a note',
  copy_artifact_to_entity: 'Copying a note',
  batch_create_artifacts: 'Authoring notes',
  delete_artifact: 'Deleting a note',
  restore_artifact: 'Restoring from Trash',
  // Collections
  find_collection_by_name: 'Finding a collection',
  list_collections: 'Browsing collections',
  get_collection: 'Reading a collection',
  create_collection: 'Creating a collection',
  update_collection: 'Updating a collection',
  // Attachments (file-op tools)
  read_file_content: 'Reading a file',
  analyze_spreadsheet: 'Analyzing a spreadsheet',
  // Native (live) spreadsheets: the spreadsheet specialist's tools.
  inspect_spreadsheet: 'Reading the spreadsheet',
  evaluate_spreadsheet_formulas: 'Working out formulas',
  write_spreadsheet_row: 'Updating a row',
  apply_spreadsheet_ops: 'Updating the spreadsheet',
  transcribe_attachment: 'Transcribing audio',
  get_upload_instructions: 'Preparing an upload',
  process_attachments: 'Processing files',
  // Links
  link_entities: 'Linking',
  get_linked_entities: 'Looking up links',
  unlink_entities: 'Unlinking',
  // Activity
  list_activity: 'Reading activity',
  // External research — flat sources
  orcid_list_works: 'Searching ORCID',
  openalex_search_works: 'Searching OpenAlex',
  openalex_fetch_by_doi: 'Fetching from OpenAlex',
  semantic_scholar_search: 'Searching Semantic Scholar',
  semantic_scholar_fetch_by_doi: 'Fetching from Semantic Scholar',
  semantic_scholar_references: 'Tracing references',
  semantic_scholar_citations: 'Tracing citations',
  semantic_scholar_recommendations: 'Finding similar papers',
  crossref_search: 'Searching Crossref',
  crossref_fetch_by_doi: 'Fetching from Crossref',
  unpaywall_resolve_oa: 'Finding open-access copy',
  arxiv_search: 'Searching arXiv',
  arxiv_fetch_by_id: 'Fetching from arXiv',
  europe_pmc_search: 'Searching Europe PMC',
  europe_pmc_fetch_full_text: 'Fetching full text',
  // External research — meta-tools (fan out across multiple sources)
  citation_trail: 'Walking the citation graph',
  scholarly_search: 'Searching scholarly literature',
  // Connectors
  connector_save_external: 'Saving to your workspace',
};

const COMPOSIO_TOOLKIT_LABELS: Readonly<Record<string, string>> = {
  GMAIL: 'Using Gmail',
  SLACK: 'Using Slack',
  GITHUB: 'Using GitHub',
  NOTION: 'Using Notion',
  LINEAR: 'Using Linear',
  GOOGLECALENDAR: 'Using Google Calendar',
  GOOGLEDRIVE: 'Using Google Drive',
  GOOGLEDOCS: 'Using Google Docs',
  GOOGLESHEETS: 'Using Google Sheets',
  HUBSPOT: 'Using HubSpot',
};

const AGENT_LABELS: Readonly<Record<string, string>> = {
  research_assistant: 'Researching',
  writer_assistant: 'Drafting a response',
  vision_advisor: 'Analyzing image',
  account_manager: 'Managing account',
  help_assistant: 'Looking up help docs',
  bower_root: 'Thinking',
};

/** Map a tool name to a user-facing label. */
export function toolLabel(toolName: string): string {
  if (TOOL_LABELS[toolName]) return TOOL_LABELS[toolName];
  const prefix = toolName.split('_', 1)[0]?.toUpperCase();
  if (prefix && COMPOSIO_TOOLKIT_LABELS[prefix]) return COMPOSIO_TOOLKIT_LABELS[prefix];
  return FALLBACK;
}

/** Map a sub-agent name to a user-facing label. */
export function agentLabel(agentName: string): string {
  return AGENT_LABELS[agentName] ?? FALLBACK;
}

// Exact-match icons take precedence over prefix-based heuristics below.
const EXACT_ICONS: Readonly<Record<string, string>> = {
  web_search: 'language',
  capture_photo: 'photo_camera',
  load_memory: 'psychology',
  sequence_analysis: 'genetics',
  bio_lookup: 'database',
  citation_trail: 'account_tree',
  scholarly_search: 'travel_explore',
  unpaywall_resolve_oa: 'lock_open',
  read_file_content: 'description',
};

const RESEARCH_PREFIXES = [
  'semantic_scholar_',
  'crossref_',
  'arxiv_',
  'europe_pmc_',
  'openalex_',
  'orcid_',
] as const;

const PREFIX_ICONS: Readonly<Array<readonly [string, string]>> = [
  ['list_', 'search'],
  ['find_', 'search'],
  ['get_', 'visibility'],
  ['create_', 'add_circle'],
  ['update_', 'edit'],
  ['move_', 'edit'],
  ['copy_', 'edit'],
  ['link_', 'link'],
  ['unlink_', 'link'],
];

/** Material Symbols icon name per tool — falls back to a generic spark. */
export function toolIcon(toolName: string): string {
  if (EXACT_ICONS[toolName]) return EXACT_ICONS[toolName];
  if (RESEARCH_PREFIXES.some((p) => toolName.startsWith(p))) return 'science';
  const prefixMatch = PREFIX_ICONS.find(([p]) => toolName.startsWith(p));
  if (prefixMatch) return prefixMatch[1];
  if (toolName.includes('search')) return 'search';
  if (toolName.includes('attachment')) return 'description';
  if (toolName.includes('transcribe')) return 'graphic_eq';
  if (toolName.includes('spreadsheet')) return 'table_chart';
  const prefix = toolName.split('_', 1)[0]?.toUpperCase();
  if (prefix && COMPOSIO_TOOLKIT_LABELS[prefix]) return 'extension';
  return 'auto_awesome';
}

/** Material Symbols icon name per sub-agent. */
export function agentIcon(agentName: string): string {
  if (agentName === 'research_assistant') return 'science';
  if (agentName === 'writer_assistant') return 'edit_note';
  if (agentName === 'vision_advisor') return 'image_search';
  if (agentName === 'help_assistant') return 'help';
  if (agentName === 'account_manager') return 'account_circle';
  return 'auto_awesome';
}
